// Sovereign Offline Emergency Single-File Interactive HTML Vault Generator
// Generates a 100% self-contained, air-gapped HTML file with embedded search and responsive dark UI

export function generateStandaloneHtml(items = [], folders = []) {
  const timestamp = new Date().toISOString().split('T')[0];
  const itemsJson = JSON.stringify(items).replace(/</g, '\\u003c');
  const foldersJson = JSON.stringify(folders).replace(/</g, '\\u003c');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>MeMark Sovereign Vault (Offline Archive)</title>
  <style>
    :root {
      --bg: #09090d;
      --card: #13131a;
      --border: #232330;
      --accent: #10b981;
      --accent-glow: rgba(16, 185, 129, 0.2);
      --text: #f3f4f6;
      --muted: #9ca3af;
    }
    * { box-sizing: border-box; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace, sans-serif; }
    body { background-color: var(--bg); color: var(--text); padding: 2rem 1.5rem; min-height: 100vh; }
    .container { max-width: 1200px; margin: 0 auto; }
    header { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 1px solid var(--border); gap: 1rem; }
    .brand { display: flex; align-items: center; gap: 0.75rem; }
    .logo { width: 36px; height: 36px; border-radius: 10px; background: var(--accent-glow); border: 1px solid var(--accent); display: flex; align-items: center; justify-content: center; font-size: 1.2rem; }
    .title h1 { font-size: 1.25rem; font-weight: 700; letter-spacing: -0.02em; }
    .title p { font-size: 0.75rem; color: var(--muted); font-family: monospace; }
    .search-box { width: 100%; max-width: 400px; position: relative; }
    .search-box input { width: 100%; background: var(--card); border: 1px solid var(--border); border-radius: 12px; padding: 0.65rem 1rem 0.65rem 2.25rem; color: var(--text); font-size: 0.85rem; outline: none; transition: border-color 0.2s; }
    .search-box input:focus { border-color: var(--accent); }
    .search-icon { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--muted); font-size: 0.85rem; }
    .stats-bar { display: flex; gap: 1rem; margin-bottom: 1.5rem; font-size: 0.75rem; font-family: monospace; color: var(--muted); }
    .stats-pill { background: var(--card); border: 1px solid var(--border); padding: 0.35rem 0.75rem; rounded: 8px; border-radius: 8px; }
    .stats-pill span { color: var(--accent); font-weight: bold; }
    .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 1rem; }
    .card { background: var(--card); border: 1px solid var(--border); border-radius: 14px; padding: 1rem; transition: transform 0.15s, border-color 0.15s, box-shadow 0.15s; display: flex; flex-direction: column; text-decoration: none; color: inherit; }
    .card:hover { transform: translateY(-2px); border-color: var(--accent); box-shadow: 0 4px 20px var(--accent-glow); }
    .card-top { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.75rem; }
    .card-icon { width: 36px; height: 36px; border-radius: 8px; background: rgba(255,255,255,0.05); border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: bold; color: var(--accent); overflow: hidden; flex-shrink: 0; }
    .card-title { font-size: 0.9rem; font-weight: 600; line-height: 1.25; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
    .card-url { font-size: 0.75rem; color: var(--muted); font-family: monospace; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-bottom: 0.5rem; }
    .card-tags { display: flex; flex-wrap: wrap; gap: 0.25rem; margin-top: auto; }
    .tag { font-size: 0.7rem; font-family: monospace; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); padding: 0.15rem 0.4rem; border-radius: 4px; color: var(--muted); }
    .empty { grid-column: 1 / -1; text-align: center; padding: 4rem 1rem; color: var(--muted); font-size: 0.9rem; }
  </style>
</head>
<body>
  <div class="container">
    <header>
      <div class="brand">
        <div class="logo">🌌</div>
        <div class="title">
          <h1>MeMark Sovereign Vault</h1>
          <p>Offline Air-Gapped Archive • Exported ${timestamp}</p>
        </div>
      </div>
      <div class="search-box">
        <span class="search-icon">🔍</span>
        <input type="text" id="search" placeholder="Filter bookmarks by title, URL or #tag...">
      </div>
    </header>

    <div class="stats-bar">
      <div class="stats-pill">Total Links: <span id="count">0</span></div>
      <div class="stats-pill">Status: <span>Air-Gap Standalone</span></div>
      <div class="stats-pill">Zero Cloud Dependencies</div>
    </div>

    <div class="grid" id="grid"></div>
  </div>

  <script>
    const items = ${itemsJson};
    const grid = document.getElementById('grid');
    const search = document.getElementById('search');
    const count = document.getElementById('count');

    function render(list) {
      grid.innerHTML = '';
      count.textContent = list.length;
      if (list.length === 0) {
        grid.innerHTML = '<div class="empty">No matching bookmarks found in this archive.</div>';
        return;
      }
      list.forEach(item => {
        const card = document.createElement('a');
        card.className = 'card';
        card.href = item.url || '#';
        card.target = '_blank';
        card.rel = 'noopener noreferrer';

        const domain = (item.url || '').replace(/^https?:\\/\\//, '').replace(/^www\\./, '').split('/')[0] || 'Link';
        const initial = domain.slice(0, 2).toUpperCase();

        const tagsHtml = (item.tags || []).map(t => '<span class="tag">' + (t.startsWith('#') ? t : '#' + t) + '</span>').join('');

        card.innerHTML = \`
          <div class="card-top">
            <div class="card-icon">\${item.logoUrl ? '<img src="' + item.logoUrl + '" style="width:100%;height:100%;object-fit:contain;" onerror="this.style.display=\\'none\\';this.nextSibling.style.display=\\'flex\\';">' + '<span style="display:none;">' + initial + '</span>' : '<span>' + initial + '</span>'}</div>
            <div class="card-title">\${item.title || 'Untitled'}</div>
          </div>
          <div class="card-url">\${domain}</div>
          <div class="card-tags">\${tagsHtml}</div>
        \`;
        grid.appendChild(card);
      });
    }

    render(items);

    search.addEventListener('input', (e) => {
      const q = e.target.value.toLowerCase().trim();
      if (!q) {
        render(items);
        return;
      }
      const filtered = items.filter(it => {
        const text = (it.title + ' ' + it.url + ' ' + (it.tags || []).join(' ')).toLowerCase();
        return text.includes(q);
      });
      render(filtered);
    });
  </script>
</body>
</html>`;
}

export function exportStandaloneHtml(items = [], folders = []) {
  const html = generateStandaloneHtml(items, folders);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const dateStr = new Date().toISOString().split('T')[0];
  const a = document.createElement('a');
  a.href = url;
  a.download = `MeMark-Sovereign-Vault-${dateStr}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const downloadStandaloneVault = exportStandaloneHtml;

